<!--
 * @Description: 
 * @Autor: luckymiaow
 * @Date: 2022-04-25 15:29:11
 * @LastEditors: luckymiaow
-->
<script lang="ts">
import type { PaginationProps, TablePaginationConfig } from 'ant-design-vue';
import { Table as ATable, message } from 'ant-design-vue';
import type { PropType } from 'vue';
import { computed, defineComponent, nextTick, onMounted, ref, watch, watchEffect } from 'vue';
import type { Api, Page, RecordType, TableBaseProps } from '../types';
import useConfigProvider from '../../config-provider/src/useConfigProvider';

import useSlotsHooks from '../../hooks/useSlot';

export default defineComponent({
  components: { ATable },
  inheritAttrs: false,
  props: {
    api: {
      type: Function as PropType<Api>,
      default: null,
    },
    page: {
      type: Object as PropType<Page>,
      default: () => ({ limit: 10, offset: 0 }),
    },
    getParams: Object,
    postParams: Object,
    dataSource: {
      type: Array as PropType<RecordType[]>,
    },
    immediate: {
      type: Boolean,
      default: false,
    },

    pagination: {
      default: null,
      type: [Boolean, Object] as PropType<false | TablePaginationConfig>,
    },
  },
  emits: ['update:data-source', 'change', 'pageChange'],
  setup(props, { attrs, emit, slots, expose }) {
    const injectConfig = useConfigProvider().injectConfig<TableBaseProps>('table');

    const pagination_in = ref<PaginationProps>({
      current: 1,
      total: 0,
      pageSize: 10,
      pageSizeOptions: ['8', '10', '20', '40'],
      showSizeChanger: true,
      showQuickJumper: true,
    });

    const dataSource_in = ref<RecordType[]>([]);

    watchEffect(() => {
      dataSource_in.value = props.dataSource || [];
    });

    const tableRef = ref<InstanceType<typeof ATable>>();

    const apiIsPageType = ref(true); // api是分页

    const page_in = ref({ limit: 10, offset: 0 });

    watchEffect(() => {
      if (props.page) {
        page_in.value.limit = props.page.limit;
        page_in.value.offset = props.page.offset;
      }
    });

    watchEffect(() => {
      if (typeof props.getParams == 'object') {
        if ('limit' in props.getParams) page_in.value.limit = props.getParams.limit;
        if ('offset' in props.getParams) page_in.value.offset = props.getParams.offset;
      }
    });

    watch(
      () => props.pagination,
      () => {
        if ((props.pagination as any) === false) {
          pagination_in.value = props.pagination as any;
        } else if (props.api == null && props.pagination != null) {
          pagination_in.value = props.pagination as any;
        } else {
          const pagination = props.pagination as any;
          for (const key in pagination) {
            if (key !== 'total') (pagination_in.value as any)[key] = pagination[key];
          }
        }
      },
      { immediate: true }
    );

    const initPagination = () => {
      if (pagination_in.value) {
        if (page_in.value.offset === 0) pagination_in.value.current = 1;
        pagination_in.value.showTotal = (total: number) =>
          `共${total}条记录 第${pagination_in.value.current}/${Math.ceil(total / pagination_in.value.pageSize!)}页`;
      }
    };
    const spinning = ref(false);

    const getData = async (perception = true) => {
      if (pagination_in.value != null) initPagination();
      if (props.api != null) {
        if (perception) spinning.value = true;
        try {
          const data = await props.api({ ...props.getParams, ...page_in.value }, props.postParams!);
          if ('items' in data) {
            pagination_in.value.pageSize = page_in.value.limit ?? 10;
            pagination_in.value.total = data?.totals ?? 0;

            apiIsPageType.value = true;
            dataSource_in.value = data.items || [];
          } else if (Array.isArray(data)) {
            apiIsPageType.value = false;
            dataSource_in.value = data;
          }
          emit('update:data-source', dataSource_in.value);
        } catch (err: any) {
          message.error({ content: err?.message ? err?.message : err.toString() });
        } finally {
          spinning.value = false;
        }
      }
    };

    const pageChange = async (p: PaginationProps, filters: any, sorter: any, data: any) => {
      if (pagination_in.value) {
        const fn = async () => {
          const { current, pageSize } = p;
          if (current == null) return;
          pagination_in.value.current = current;
          page_in.value.offset = (pagination_in.value.current - 1) * pageSize!;
          page_in.value.limit = pageSize!;
          if (apiIsPageType.value) await getData();
          pagination_in.value.current = pagination_in.value.current!;
          pagination_in.value.pageSize = pageSize;
        };
        if (pagination_in.value.current !== p.current || pagination_in.value.pageSize !== p.pageSize) {
          await fn();
          emit('pageChange', pagination_in.value, filters, sorter, data, fn);
        } else {
          emit('change', pagination_in.value, filters, sorter, data, fn);
        }
      } else {
        emit('change', pagination_in.value, filters, sorter, data, getData);
      }
    };

    const getBindValue = computed(() => ({
      ...injectConfig?.value,
      ...attrs,
      onChange: pageChange,
    }));

    onMounted(async () => {
      await nextTick();
      await initPagination();
      if (props.immediate) {
        await getData();
      }
    });

    async function refreshData(perception = false) {
      await getData(perception);
    }

    async function fetchData() {
      if (pagination_in.value) {
        pagination_in.value.current = 1;
        page_in.value.offset = 0;
      }
      await getData();
    }

    const getSerialNumber = (index: number) => {
      const { current = 0, pageSize = 0 } = pagination_in.value;

      return pagination_in.value ? (current - 1) * pageSize + index + 1 : index + 1;
    };

    const { useExcludeSlots } = useSlotsHooks({ slots }, ['default', 'title']);

    return {
      pagination_in,
      dataSource_in,
      fetchData,
      refreshData,
      spinning,
      pageChange,
      getSerialNumber,
      injectConfig,
      getBindValue,
      useExcludeSlots,
      tableRef,
    };
  },
});
</script>

<template>
  <ATable
    v-bind="getBindValue"
    ref="tableRef"
    :loading="spinning"
    :data-source="dataSource_in"
    :pagination="pagination_in"
    @change="pageChange"
    ><slot></slot>
    <template v-for="(slot, key) in useExcludeSlots" :key="key" #[key]="scope">
      <slot :name="key" v-bind="scope ?? {}" :serial-number="getSerialNumber(scope.index)"> </slot>
    </template>
  </ATable>
</template>
